import { CheckIcon, ChevronsUpDown } from 'lucide-react'
import { forwardRef, useCallback, useEffect, useState } from 'react'
import {
  type Country,
  type FlagProps,
  getCountryCallingCode,
  default as RPNInput,
  type Value,
} from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

type PhoneInputProps = Omit<
  React.ComponentProps<'input'>,
  'onChange' | 'value' | 'ref'
> &
  Omit<React.ComponentProps<typeof RPNInput>, 'onChange'> & {
    onChange?: (value: Value) => void
  }

const PhoneInput = forwardRef<
  React.ElementRef<typeof RPNInput>,
  PhoneInputProps
>(({ className, onChange, ...props }, ref) => {
  const handleChange = useCallback(
    (value: Value) => {
      // Sempre chama o onChange com o valor, mesmo que seja undefined
      onChange?.(value || ('' as Value))
    },
    [onChange]
  )

  return (
    <RPNInput
      className={cn('flex', className)}
      countries={['BR']}
      countrySelectComponent={CountrySelect}
      defaultCountry="BR"
      flagComponent={FlagComponent}
      inputComponent={InputComponent}
      /**
       * Handles the onChange event.
       *
       * react-phone-number-input might trigger the onChange event as undefined
       * when a valid phone number is not entered. To prevent this,
       * the value is coerced to an empty string.
       *
       * @param {E164Number | undefined} value - The entered value
       */
      international={false}
      onChange={handleChange}
      ref={ref}
      smartCaret={true}
      {...props}
    />
  )
})
PhoneInput.displayName = 'PhoneInput'

// Constantes para formatação de telefone
const PHONE_LENGTHS = {
  DDD: 2,
  PHONE_WITHOUT_9: 6,
  PHONE_WITH_9: 10,
  MAX_DIGITS: 11,
  MAX_DISPLAY_LENGTH: 15,
} as const

const InputComponent = forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>(({ className, onChange, value, ...props }, ref) => {
  const [displayValue, setDisplayValue] = useState(String(value || ''))

  // Função para formatar número brasileiro
  const formatBrazilianPhone = useCallback((phone: string) => {
    const cleaned = phone.replace(/\D/g, '')

    if (cleaned.length <= PHONE_LENGTHS.DDD) {
      return cleaned
    }
    if (cleaned.length <= PHONE_LENGTHS.PHONE_WITHOUT_9) {
      return `(${cleaned.slice(0, PHONE_LENGTHS.DDD)}) ${cleaned.slice(PHONE_LENGTHS.DDD)}`
    }
    if (cleaned.length <= PHONE_LENGTHS.PHONE_WITH_9) {
      // Formato: (XX) XXXX-XXXX para números de 8 dígitos
      const phonePart = cleaned.slice(PHONE_LENGTHS.DDD)
      if (phonePart.length <= 4) {
        return `(${cleaned.slice(0, PHONE_LENGTHS.DDD)}) ${phonePart}`
      }
      return `(${cleaned.slice(0, PHONE_LENGTHS.DDD)}) ${phonePart.slice(0, 4)}-${phonePart.slice(4)}`
    }
    // Formato: (XX) 9XXXX-XXXX para números de 9 dígitos (com o 9)
    const phonePart = cleaned.slice(PHONE_LENGTHS.DDD)
    if (phonePart.length <= 5) {
      return `(${cleaned.slice(0, PHONE_LENGTHS.DDD)}) ${phonePart}`
    }
    return `(${cleaned.slice(0, PHONE_LENGTHS.DDD)}) ${phonePart.slice(0, 5)}-${phonePart.slice(5)}`
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const cleaned = inputValue.replace(/\D/g, '')

    // Limita a 11 dígitos (DDD + 9 dígitos)
    if (cleaned.length <= PHONE_LENGTHS.MAX_DIGITS) {
      const formatted = formatBrazilianPhone(cleaned)
      setDisplayValue(formatted)

      // Chama o onChange original com o valor limpo
      onChange?.({
        ...e,
        target: { ...e.target, value: cleaned },
      })
    }
  }

  // Atualiza o valor de exibição quando o valor externo muda
  useEffect(() => {
    const stringValue = String(value || '')
    if (stringValue !== displayValue) {
      const formatted = formatBrazilianPhone(stringValue)
      setDisplayValue(formatted)
    }
  }, [value, displayValue, formatBrazilianPhone])

  return (
    <Input
      className={cn('rounded-s-none rounded-e-lg', className)}
      {...props}
      maxLength={PHONE_LENGTHS.MAX_DISPLAY_LENGTH}
      onChange={handleChange}
      placeholder="(11) 99999-9999"
      ref={ref}
      value={displayValue}
    />
  )
})
InputComponent.displayName = 'InputComponent'

type CountryEntry = { label: string; value: Country | undefined }

type CountrySelectProps = {
  disabled?: boolean
  value: Country
  options: CountryEntry[]
  onChange: (country: Country) => void
}

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          className="flex max-w-[80px] gap-1 rounded-s-lg rounded-e-none border-r-0 px-3 focus:z-10"
          disabled={disabled}
          type="button"
          variant="outline"
        >
          <FlagComponent
            country={selectedCountry}
            countryName={selectedCountry}
          />
          <ChevronsUpDown
            className={cn(
              '-mr-2 size-4 opacity-50',
              disabled ? 'hidden' : 'opacity-100'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder="Search country..." />
          <CommandList>
            <ScrollArea className="h-72">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      country={value}
                      countryName={label}
                      key={value}
                      onChange={onChange}
                      selectedCountry={selectedCountry}
                    />
                  ) : null
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface CountrySelectOptionProps extends FlagProps {
  selectedCountry: Country
  onChange: (country: Country) => void
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
}: CountrySelectOptionProps) => {
  return (
    <CommandItem className="gap-2" onSelect={() => onChange(country)}>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-foreground/50 text-sm">{`+${getCountryCallingCode(country)}`}</span>
      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? 'opacity-100' : 'opacity-0'}`}
      />
    </CommandItem>
  )
}

const FlagComponent = ({ country, countryName }: FlagProps) => {
  const Flag = flags[country as keyof typeof flags]

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20">
      {Flag && <Flag title={countryName} />}
    </span>
  )
}

export { PhoneInput }
